import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCityPrice } from "../../hooks/useCityPrice";
import { isServiceEnabled } from "../../config";
import { TECHNICAL_SERVICES } from "../../config/pricing";
import CategorySidebar from "./CategorySidebar";

import electrician_img from "../../components/images/stage_01/Electrican_labour.png";
import switch_socket_img from "../../components/images/stage_01/switch and socket.png";
import switchboard_img from "../../components/images/stage_01/switchboard_main.png";
import electrical_img from "../../components/images/stage_01/electrical.png";
import painting_img from "../../components/images/stage_01/painting_service.png";
import ac_service_img from "../../components/images/stage_01/ac_service.png";

const fallbackImage =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";

// Booking destination for every card on this page — clicking "Book Now" on
// ANY service always hands off to the Schedule Booking wizard's own
// Technical category, whose own service list takes over for the rest of
// the flow (no card links anywhere else, including Switch & Socket Repair,
// which used to jump straight to its own dedicated SwitchSocketBookingPage).
const TECHNICAL_WIZARD_LINK = "/schedule-booking/technical";

// Consultation is NOT the same fixed-price flow as the other cards — it's
// its own quotation-based inspect-first-price-after booking, with its own
// dedicated page and backend (schedule_technical_consultation collection).
const CONSULTATION_LINK = "/services/technical-services/consultation";

// ── Top category pills. "electrical" is the one populated with its own
// curated 3-service list (per the reference design); the rest still show
// their single existing TECHNICAL_SERVICES entry until given the same
// treatment. ─────────────────────────────────────────────────────────────
const MAIN_CATEGORIES = [
  { key: "electrical", label: "Electrical", icon: "⚡" },
  { key: "plumbing",   label: "Plumbing",   icon: "💧" },
  { key: "painter",    label: "Painter",    icon: "🖌️" },
];
const MORE_CATEGORIES = [
  { key: "ac",        label: "AC Service",  icon: "❄️" },
  { key: "carpenter", label: "Carpenter",   icon: "🪚" },
  { key: "pest",      label: "Pest Control",icon: "🐜" },
];
const ALL_CATEGORIES = [...MAIN_CATEGORIES, ...MORE_CATEGORIES];

// Categories not populated with real service content yet — show a
// "Coming Soon" placeholder instead of their (currently single, generic)
// service card.
const COMING_SOON_CATEGORIES = ["plumbing", "painter", "pest", "carpenter"];

const CATEGORY_SERVICES = {
  electrical: [
    {
      id: "tech-2-switch-socket", name: "Switch & Socket",
      desc: "Install, replace or consult on switches and sockets",
      price: 149, duration: "30–60 min", rating: 4.8,
      image: switch_socket_img, link: TECHNICAL_WIZARD_LINK,
    },
    {
      id: "tech-2-switchboard", name: "Switchboard",
      desc: "Install, replace or repair switchboards and switch boxes",
      price: 199, duration: "30–45 min", rating: 4.8,
      image: switchboard_img, link: TECHNICAL_WIZARD_LINK,
    },
    {
      id: "tech-2-consultation", name: "Consultation",
      desc: "Not sure what you need? An electrician inspects and advises",
      price: 90, duration: "15–20 min", rating: 4.8,
      image: electrician_img, link: CONSULTATION_LINK,
    },
    {
      id: "tech-2-all-services", name: "All Services",
      desc: "Browse the full list of electrical services",
      image: electrical_img, link: TECHNICAL_WIZARD_LINK, isBrowseAll: true,
    },
  ],
  plumbing: [
    {
      id: "tech-1", name: "Plumbing Service", desc: "Leak repair, fittings and more",
      price: TECHNICAL_SERVICES["tech-1"]?.price, duration: TECHNICAL_SERVICES["tech-1"]?.duration,
      rating: 4.7, image: fallbackImage, link: TECHNICAL_WIZARD_LINK,
    },
  ],
  painter: [
    {
      id: "tech-5", name: "Painting Service", desc: "Wall painting and touch-ups",
      price: TECHNICAL_SERVICES["tech-5"]?.price, duration: TECHNICAL_SERVICES["tech-5"]?.duration,
      rating: 4.7, image: painting_img, link: TECHNICAL_WIZARD_LINK,
    },
  ],
  ac: [
    {
      id: "tech-3", name: "AC Service", desc: "Installation, repair and cleaning",
      price: TECHNICAL_SERVICES["tech-3"]?.price, duration: TECHNICAL_SERVICES["tech-3"]?.duration,
      rating: 4.7, image: ac_service_img, link: TECHNICAL_WIZARD_LINK,
    },
  ],
  carpenter: [
    {
      id: "tech-4", name: "Carpenter Service", desc: "Furniture repair and woodwork",
      price: TECHNICAL_SERVICES["tech-4"]?.price, duration: TECHNICAL_SERVICES["tech-4"]?.duration,
      rating: 4.6, image: fallbackImage, link: TECHNICAL_WIZARD_LINK,
    },
  ],
  pest: [
    {
      id: "tech-9", name: "Pest Control", desc: "Home pest treatment and prevention",
      price: TECHNICAL_SERVICES["tech-9"]?.price, duration: TECHNICAL_SERVICES["tech-9"]?.duration,
      rating: 4.6, image: fallbackImage, link: TECHNICAL_WIZARD_LINK,
    },
  ],
};

// "You may also need" cross-links — switches the active tab on this same
// page rather than navigating away, since every category here already
// lives on this one page.
const YOU_MAY_ALSO_NEED = [
  { key: "plumbing", name: "Plumbing", desc: "Leak repair, fittings and more", image: fallbackImage },
  { key: "painter",  name: "Painter",  desc: "Wall painting and touch-ups",    image: painting_img },
  { key: "ac",       name: "AC Service", desc: "Installation, repair and cleaning", image: ac_service_img },
];

const TechnicalServices = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();
  const [activeCategory, setActiveCategory] = useState("electrical");
  const [showMore, setShowMore] = useState(false);

  const activeCategoryMeta = ALL_CATEGORIES.find((c) => c.key === activeCategory);
  // A service the Dashboard's Enable/Disable tab turns off is left out of
  // this list entirely (not shown disabled) — "Browse All" is a navigation
  // card, not a toggleable service, so it's always kept.
  const activeServices = (CATEGORY_SERVICES[activeCategory] || [])
    .filter((service) => service.isBrowseAll || isServiceEnabled(String(service.id)));
  const isComingSoon = COMING_SOON_CATEGORIES.includes(activeCategory);

  const handleBookNow = (service) => navigate(service.link || TECHNICAL_WIZARD_LINK);

  return (
    <div style={styles.page}>
      <div style={styles.layout}>

        <aside style={styles.sidebar}>
          <CategorySidebar />

          <div style={styles.helpCard}>
            <h4 style={styles.helpTitle}>Need Help with Technical Work?</h4>
            <p style={styles.helpText}>Our trained experts are ready to assist you quickly.</p>
            <Link to="/contact-us" style={{ ...styles.chatBtn, display: "inline-block", textDecoration: "none", textAlign: "center" }}>💬 Chat with Technician</Link>
            <div style={styles.helpIcon}>🛠️</div>
          </div>
        </aside>

        <main style={styles.main}>
          <div style={styles.breadcrumb}>Home &gt; Technical Services</div>

          <h1 style={styles.pageTitle}>Technical Services</h1>
          <p style={styles.pageSubtitle}>Expert help for repairs, installations and maintenance.</p>

          <div style={styles.tabRow}>
            {MAIN_CATEGORIES.map((c) => (
              <button
                key={c.key}
                style={{ ...styles.tabBtn, ...(activeCategory === c.key ? styles.tabBtnActive : {}) }}
                onClick={() => setActiveCategory(c.key)}
              >
                <span>{c.icon}</span> {c.label}
              </button>
            ))}
            <button style={styles.tabBtn} onClick={() => setShowMore((v) => !v)}>
              + More
            </button>
            {showMore && MORE_CATEGORIES.map((c) => (
              <button
                key={c.key}
                style={{ ...styles.tabBtn, ...(activeCategory === c.key ? styles.tabBtnActive : {}) }}
                onClick={() => setActiveCategory(c.key)}
              >
                <span>{c.icon}</span> {c.label}
              </button>
            ))}
          </div>

          <h2 style={styles.sectionTitle}>{activeCategoryMeta?.label} Services</h2>

          {isComingSoon ? (
            <div style={styles.comingSoonCard}>
              <div style={styles.comingSoonIcon}>{activeCategoryMeta?.icon}</div>
              <h3 style={styles.comingSoonTitle}>Coming Soon</h3>
              <p style={styles.comingSoonText}>
                {activeCategoryMeta?.label} Services aren't available for booking yet — check back soon.
              </p>
            </div>
          ) : (
          <div style={styles.serviceGrid}>
            {activeServices.map((service) => {
              const price = cityPrice(String(service.id), service.price) || service.price;
              return (
                <div key={service.id} style={styles.serviceCard}>
                  <div style={styles.serviceImageWrap}>
                    <img
                      src={service.image}
                      alt={service.name}
                      style={styles.serviceImage}
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
                    />
                    {!service.isBrowseAll && <span style={styles.ratingBadge}>⭐ {service.rating}</span>}
                  </div>
                  <div style={styles.serviceBody}>
                    <h3 style={styles.serviceName}>{service.name}</h3>
                    <p style={styles.serviceDesc}>{service.desc}</p>
                    {!service.isBrowseAll && (
                      <>
                        <div style={styles.priceRow}>
                          <span style={styles.priceLabel}>From</span>
                          <span style={styles.price}>₹{price}</span>
                        </div>
                        <div style={styles.metaRow}>
                          <span style={styles.verifiedPill}>✅ Verified professional</span>
                          <span style={styles.durationPill}>🕐 {service.duration}</span>
                        </div>
                      </>
                    )}
                    <button
                      style={styles.bookBtn}
                      onClick={() => handleBookNow(service)}
                    >
                      {service.isBrowseAll ? "Browse All  →" : "Book Now  →"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          <h3 style={styles.gallerySectionTitle}>See our professionals at work</h3>
          <div style={styles.galleryGrid}>
            <div style={styles.galleryCard}>
              <img src={electrical_img} alt="Electrical inspection" style={styles.galleryImage}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
              <div style={styles.galleryCaption}>Electrical inspection</div>
            </div>
            <div style={styles.galleryCard}>
              <img src={electrician_img} alt="Installation support" style={styles.galleryImage}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
              <div style={styles.galleryCaption}>Installation support</div>
            </div>
          </div>

          <h3 style={styles.gallerySectionTitle}>You may also need</h3>
          <div style={styles.alsoNeedGrid}>
            {YOU_MAY_ALSO_NEED.map((item) => (
              <button key={item.key} style={styles.alsoNeedCard} onClick={() => setActiveCategory(item.key)}>
                <img src={item.image} alt={item.name} style={styles.alsoNeedImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div>
                  <div style={styles.alsoNeedName}>{item.name}</div>
                  <div style={styles.alsoNeedDesc}>{item.desc}</div>
                  <span style={styles.alsoNeedLink}>Explore →</span>
                </div>
              </button>
            ))}
          </div>

          <div style={styles.ctaBar}>
            <div style={styles.ctaLeft}>
              <span style={styles.ctaIcon}>💬</span>
              <div>
                <div style={styles.ctaTitle}>Not sure what service you need?</div>
                <div style={styles.ctaText}>Tell us your requirement and we'll help you choose the right service.</div>
              </div>
            </div>
            <Link to="/contact-us" style={styles.ctaBtn}>Get Help →</Link>
          </div>
        </main>

      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    color: "#111827"
  },
  layout: {
    maxWidth: "1300px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "250px 1fr",
    gap: "20px",
    alignItems: "start"
  },
  sidebar: {
    display: "flex",
    flexDirection: "column",
    gap: "18px"
  },
  helpCard: {
    background: "#f5f3ff",
    border: "1px solid #ddd6fe",
    borderRadius: "18px",
    padding: "18px",
    textAlign: "left"
  },
  helpTitle: {
    margin: "0 0 8px",
    fontSize: "18px",
    fontWeight: 700
  },
  helpText: {
    margin: "0 0 14px",
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: 1.6
  },
  chatBtn: {
    border: "none",
    background: "#fff",
    color: "#4f46e5",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "12px 16px",
    cursor: "pointer"
  },
  helpIcon: {
    fontSize: "54px",
    textAlign: "center",
    marginTop: "18px"
  },
  main: {
    minWidth: 0
  },
  breadcrumb: {
    fontSize: "13px",
    color: "#9ca3af",
    marginBottom: "14px"
  },
  pageTitle: {
    margin: 0,
    fontSize: "42px",
    fontWeight: 800,
  },
  pageSubtitle: {
    margin: "8px 0 22px",
    fontSize: "15px",
    color: "#6b7280",
  },
  tabRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "26px",
  },
  tabBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    fontWeight: 700,
    fontSize: "14px",
    padding: "12px 20px",
    borderRadius: "14px",
    cursor: "pointer",
  },
  tabBtnActive: {
    background: "#4f46e5",
    color: "#fff",
    border: "1px solid #4f46e5",
  },
  sectionTitle: {
    margin: "0 0 16px",
    fontSize: "26px",
    fontWeight: 800
  },
  serviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "18px"
  },
  comingSoonCard: {
    background: "#fff",
    border: "1.5px dashed #e5e7eb",
    borderRadius: "18px",
    padding: "56px 24px",
    textAlign: "center",
  },
  comingSoonIcon: {
    fontSize: "40px",
    marginBottom: "12px",
  },
  comingSoonTitle: {
    margin: "0 0 8px",
    fontSize: "22px",
    fontWeight: 800,
  },
  comingSoonText: {
    margin: 0,
    fontSize: "14px",
    color: "#6b7280",
  },
  serviceCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    overflow: "hidden",
    position: "relative"
  },
  serviceImageWrap: {
    position: "relative",
  },
  serviceImage: {
    width: "100%",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    display: "block",
  },
  ratingBadge: {
    position: "absolute",
    top: "10px",
    right: "10px",
    background: "#fff",
    borderRadius: "999px",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: 800,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
  },
  serviceBody: {
    padding: "16px"
  },
  serviceName: {
    margin: "0 0 6px",
    fontSize: "19px",
    fontWeight: 700
  },
  serviceDesc: {
    margin: "0 0 14px",
    fontSize: "13.5px",
    lineHeight: 1.5,
    color: "#6b7280",
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    marginBottom: "12px",
  },
  priceLabel: {
    fontSize: "13px",
    color: "#6b7280",
    fontWeight: 600,
  },
  price: {
    fontSize: "24px",
    fontWeight: 800,
    color: "#4f46e5",
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "14px",
  },
  verifiedPill: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#15803d",
    background: "#e8f9ee",
    borderRadius: "999px",
    padding: "5px 10px",
  },
  durationPill: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#4b5563",
    background: "#f3f4f6",
    borderRadius: "999px",
    padding: "5px 10px",
  },
  bookBtn: {
    width: "100%",
    height: "46px",
    border: "none",
    background: "linear-gradient(135deg,#4f46e5,#4338ca)",
    color: "#fff",
    borderRadius: "12px",
    fontWeight: 700,
    fontSize: "15px",
    cursor: "pointer"
  },

  gallerySectionTitle: {
    margin: "32px 0 16px",
    fontSize: "24px",
    fontWeight: 800,
  },
  galleryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "18px",
  },
  galleryCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    overflow: "hidden",
  },
  galleryImage: {
    width: "100%",
    aspectRatio: "16 / 9",
    objectFit: "cover",
    display: "block",
  },
  galleryCaption: {
    padding: "14px 16px",
    fontWeight: 700,
    fontSize: "15px",
  },

  alsoNeedGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "16px",
  },
  alsoNeedCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    textAlign: "left",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "14px",
    cursor: "pointer",
  },
  alsoNeedImage: {
    width: "64px",
    height: "64px",
    objectFit: "cover",
    borderRadius: "12px",
    flexShrink: 0,
  },
  alsoNeedName: {
    fontSize: "15px",
    fontWeight: 700,
    marginBottom: "2px",
  },
  alsoNeedDesc: {
    fontSize: "12.5px",
    color: "#6b7280",
    marginBottom: "6px",
  },
  alsoNeedLink: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#4f46e5",
  },

  ctaBar: {
    marginTop: "24px",
    background: "#eef2ff",
    border: "1px solid #e0e7ff",
    borderRadius: "18px",
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },
  ctaLeft: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  ctaIcon: {
    fontSize: "22px",
    background: "#fff",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  ctaTitle: {
    fontWeight: 800,
    fontSize: "15px",
  },
  ctaText: {
    fontSize: "13px",
    color: "#6b7280",
  },
  ctaBtn: {
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    height: "44px",
    padding: "0 20px",
    borderRadius: "12px",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
};

export default TechnicalServices;
